import { FC, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";
import { IProfessionalSchedulesAvailable } from "../../api/getProfessionals";
import { getSchedulesAvailable, ISchedulesAvailable } from "../../api/getSchedulesAvailable";
import { formatDate, getDayOfWeekName, incrementOnDate, transformDateToType } from "../../utils/date-util/dateUtils";
import ButtonSecondary from "../buttons/ButtonSecondary";
import SliderNavigationButton from "../buttons/SliderNavigationButton";
import LazyLoad from "../LazyLoad";
import FreeTimeLoader, { DynamicFreeTimeLoader } from "../loaders/FreeTimeLoader";
import getUserData from "../utils/getUserData";

const Container = styled.div`
	display: flex;
	flex-direction: column;
	margin: 16px 18px;
	border: 1px solid #e9ecef;
	border-radius: 8px;
	background-color: ${props => (props.theme.colors.lightGrayVitat || "#f2f2f8")};
	box-shadow: 0px 4px 4px 1px #0000000a;
	min-height: 100%;
`;

const Header = styled.span`
	font-family: Livvic;
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;
	letter-spacing: 0.005em;
	text-align: center;
	color: ${props => (props.theme.colors.blackVitat || "#002b49")};
	border-bottom: 5px solid ${props => (props.theme.colors.brownVitat || "#bfe338")};
	padding: 12px 16px;
`;

const FreeTimePickerWrapper = styled.div`
	display: flex;
	position: relative;
	justify-content: center;
	padding-top: 16px;

	.swiper {
		margin: 0 40px;
		width: 100%;
		position: initial;
	}

	.swiper-slide {
		display: flex;
		flex-direction: column;
		align-items: center;
	}
`;

const ColumnHeader = styled.div``;

const HoursAvailable = styled.div`
	display: flex;
	flex-flow: column;
	align-items: center;
	overflow: auto;
	gap: 5px;
	width: 100%;

	button {
		box-shadow: none;
	}
`;

const DayWeek = styled.p`
	font-family: Livvic;
	font-style: normal;
	font-weight: normal;
	font-size: 12.5px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	margin: 0px;
`;

const DateNumeric = styled.p`
	font-family: Livvic;
	font-style: normal;
	font-weight: normal;
	font-size: 8px;
	line-height: 165.5%;
	text-align: center;
	letter-spacing: 0.005em;
	color: #000000;
	margin: 0px;
	margin-bottom: 10px;
`;

const LoadingWrapper = styled.div`
	margin: 16px 40px 0px 40px;
	width: 100%;
	display: flex;
	justify-content: center;
`;

const InfoWrapper = styled.div`
	font-family: Livvic;
	font-size: 14px;
	font-weight: 400;
	line-height: 20px;
	letter-spacing: 0em;
	text-align: center;
	color: "#6F7590";
	text-align: center;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	height: 100%;
	padding: 24px;
`;

interface FreeTimePickerProps {
	title: string;
	codProfessional: string;
	handleClick: (schedule: IProfessionalSchedulesAvailable) => void;
}

const FreeTimePicker: FC<FreeTimePickerProps> = ({ title, codProfessional, handleClick }) => {
	const [isAssignmentLoading, setIsAssignmentLoading] = useState<boolean>(false);
	const [schedulesAvailable, setSchedulesAvailable] = useState<ISchedulesAvailable[]>([]);
	const [isNextPageLoading, setIsNextPageLoading] = useState<boolean>(false);
	const [hasNextPage, setHasNextPage] = useState<boolean>(true);
	const [slidesPerView, setSlidesPerView] = useState<number>(1);
	const [searchParams] = useSearchParams();

	const userData = getUserData();
	const procedure = userData.procedureId || searchParams.get("procedure");

	const isFirstLoad = useRef<boolean>(true);
	const reqCount = useRef<number>(0);
	const startDate = useRef<string>(transformDateToType(new Date()).toISODate());

	const getSchedules = async (initialDate: string): Promise<ISchedulesAvailable[]> => {
		const dataInicial = transformDateToType(initialDate);
		const dataFinal = incrementOnDate(30, "days", dataInicial);

		const schedules = await getSchedulesAvailable(codProfessional, {
			dataFinal: dataFinal.toISODate(),
			dataInicial: dataInicial.toISODate(),
			procedureIds: procedure ? [procedure] : undefined,
		});

		return schedules;
	};

	const loadNextPage = async () => {
		console.log("loadNextPage");

		// MULTIPROCEDIMENTO
		if (!procedure) {
			setIsNextPageLoading(false);
			setHasNextPage(false);
			return;
		}

		if (!isNextPageLoading) {
			setIsNextPageLoading(true);

			const lastDate = schedulesAvailable[schedulesAvailable.length - 1]?.date;
			const newStartDate = incrementOnDate(1, "days", transformDateToType(lastDate)).toISO();

			const schedules = await getSchedules(newStartDate);
			if (schedules.length === 0) setHasNextPage(false);
			setSchedulesAvailable(prevValue => [...prevValue, ...schedules]);
			setIsNextPageLoading(false);
		}
	};

	const loadSchedules = async () => {
		reqCount.current += 1;

		if (reqCount.current > 2) {
			setIsNextPageLoading(false);
			setHasNextPage(false);
			return;
		}

		const schedules = await getSchedules(startDate.current);

		const newStartDate = incrementOnDate(31, "days", transformDateToType(startDate.current));
		startDate.current = newStartDate.toISODate();

		if (schedules.length === 0) {
			loadSchedules();
			return;
		}

		setSchedulesAvailable(schedules);
		setIsNextPageLoading(false);
	};

	useEffect(() => {
		if (!procedure) return;

		startDate.current = transformDateToType(new Date()).toISODate();
		setSchedulesAvailable([]);
		setIsNextPageLoading(true);
		setHasNextPage(true);
		reqCount.current = 0;

		loadSchedules();

		isFirstLoad.current = false;
	}, [procedure]);

	useEffect(() => {
		const handleScreenWidthChange = () => {
			let windowWidth = window.innerWidth;
			if (windowWidth >= 675) windowWidth = 675;
			setSlidesPerView(Math.floor(windowWidth / 120));
		};
		handleScreenWidthChange();
		window.addEventListener("resize", handleScreenWidthChange);
		return () => window.removeEventListener("resize", handleScreenWidthChange);
	}, []);

	return (
		<Container>
			<Header>{title}</Header>

			{schedulesAvailable.length === 0 && !isNextPageLoading ? (
				<InfoWrapper>
					{!procedure ? (
						<span>Escolha um procedimento para listar os horários.</span>
					) : (
						<>
							<span>Nenhum horário encontrado.</span>
							<span>Experimente alterar o procedimento.</span>
						</>
					)}
				</InfoWrapper>
			) : (
				<FreeTimePickerWrapper>
					{schedulesAvailable.length > 0 ? (
						<Swiper slidesPerView={slidesPerView}>
							<SliderNavigationButton prev />
							{schedulesAvailable.map((horario, index) => (
								<SwiperSlide key={index}>
									<ColumnHeader>
										<DayWeek>{getDayOfWeekName(horario.date)}</DayWeek>
										<DateNumeric>{formatDate("dd/MM", horario.date)}</DateNumeric>
									</ColumnHeader>
									<HoursAvailable>
										{horario.schedules.map((schedule, index) => (
											<ButtonSecondary
												key={`${schedule.idHorario}${index}`}
												onClick={
													!isAssignmentLoading
														? async () => {
															setIsAssignmentLoading(true);
															await handleClick(schedule);
															setIsAssignmentLoading(false);
														}
														: undefined
												}
												label={schedule.hora}
												margin={5}
											/>
										))}
									</HoursAvailable>
								</SwiperSlide>
							))}

							<SwiperSlide>
								<LazyLoad loadNextPage={loadNextPage} hasNextPage={hasNextPage} SkeletonComponent={FreeTimeLoader} direction="row" />
							</SwiperSlide>

							<SliderNavigationButton prev={false} />
						</Swiper>
					) : (
						<LoadingWrapper>
							<DynamicFreeTimeLoader quantity={slidesPerView} />
						</LoadingWrapper>
					)}
				</FreeTimePickerWrapper>
			)}
		</Container>
	);
};

export default FreeTimePicker;
